/**
 * 工程表の右の時間軸（#100、docs/39 §3.1）。日付の計算そのものは packages/contract（画面と API が同じ関数）。
 * ここは「何日目を何 px に描くか」だけ
 */
import { addDays, calendarForRow, dayNo, snapStartOf, snapToWorkDay, weekdayOf, workDaysBetween, ymdOfDayNo, type ScheduleRow, type WorkCalendar } from "@genbaryoku/contract";

/** 表示（#291 §2.1.1 で週をやめて旬＝上旬・中旬・下旬） */
export type View = "day" | "jun" | "month";
/** 覚えた表示の値を読む（前の week は旬）。知らない値は null */
export const viewOf = (v: unknown): View | null => (v === "week" ? "jun" : v === "day" || v === "jun" || v === "month" ? v : null);
/** 1日の幅（px）：日 24／旬 7.2（1旬 72px ÷ 10日。本当の幅は旬の日数で割る＝scaleOf）／月 4 */
export const PX: Record<View, number> = { day: 24, jun: 7.2, month: 4 };
/** 旬の列の幅（px。上旬・中旬・下旬とも同じ） */
export const JUN_PX = 72;
/** ‹ › で動かす日数 */
export const STEP_DAYS: Record<View, number> = { day: 7, jun: 30, month: 91 };
/**
 * 旬・月の表の少なくともの長さ（月の数）。短い{schedule}でも表を画面の横幅（1920px ほど）より広く取り、右に列の無い所を残さない
 * （#291 の本番の確かめ、11/3。旬 9か月＝27旬×72px＝1,944px・月 16か月×約120px＝約1,920px）
 */
export const MIN_MONTHS: Record<Exclude<View, "day">, number> = { jun: 9, month: 16 };
/** start の月から n か月の末日 */
const monthsEnd = (start: string, n: number): string => {
  let e = monthStartOf(start);
  for (let k = 0; k < n; k++) e = addDays(monthEndOf(e), 1);
  return addDays(e, -1);
};

/** 月の1日・末日（「2026-10-18」→「2026-10-01」「2026-10-31」） */
export const monthStartOf = (ymd: string): string => `${ymd.slice(0, 7)}-01`;
export const monthEndOf = (ymd: string): string => addDays(`${addDays(monthStartOf(ymd), 31).slice(0, 7)}-01`, -1);
/** その日の旬の日数（上旬・中旬は 10日・下旬は 21日〜末日） */
const junDays = (ymd: string): number => {
  const d = Number(ymd.slice(8, 10));
  return d <= 20 ? 10 : Number(monthEndOf(ymd).slice(8, 10)) - 20;
};

/**
 * 時間軸の物差し（#291 §2.1.1）：日の番号（範囲の最初の日＝0）→ 左端の px・その日の幅。
 *   日・月は1日の幅が同じ（px）。旬は 1旬＝JUN_PX（2本指の拡大なら px × 10）で、1日の幅＝旬の幅 ÷ その旬の日数。
 *   範囲の外の日は端の日の幅で延ばす。idxAt は px → その位置の日の番号（範囲の外も延ばす）
 */
export type TimeScale = { x: (i: number) => number; w: (i: number) => number; total: number; idxAt: (px: number) => number };
export function scaleOf(view: View, start: string, days: number, px: number): TimeScale {
  const n = Math.max(1, days);
  if (view !== "jun") {
    return { x: (i) => i * px, w: () => px, total: n * px, idxAt: (v) => Math.floor(v / px) };
  }
  const jw = px === PX.jun ? JUN_PX : px * 10;
  const base = dayNo(start);
  const ws = Array.from({ length: n }, (_, i) => jw / junDays(ymdOfDayNo(base + i)));
  const xs = [0];
  for (const w of ws) xs.push(xs[xs.length - 1]! + w);
  const total = xs[n]!;
  const x = (i: number) => (i <= 0 ? i * ws[0]! : i >= n ? total + (i - n) * ws[n - 1]! : xs[i]!);
  const w = (i: number) => ws[Math.max(0, Math.min(n - 1, i))]!;
  const idxAt = (v: number) => {
    if (v < 0) return Math.floor(v / ws[0]!);
    if (v >= total) return n + Math.floor((v - total) / ws[n - 1]!);
    let lo = 0;
    let hi = n - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (xs[mid]! <= v) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  };
  return { x, w, total, idxAt };
}
/** 行の高さ：どの行も 44（PC の明細表 44px。2段の文字が入る。#105 で作業の領域を広く＝docs/39 §4.8.2） */
export const ROW_H: Record<ScheduleRow["kind"], number> = { task: 44, milestone: 44, heading: 44, memo: 44 };

/** 今日（この端末の暦）'YYYY-MM-DD' */
export function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** 描く範囲：日付のある行と今日を含み、前に1週・後ろに4週。最低 8週。日は月曜から、月は1日から、旬は月の1日〜末日（#291 §2.1.1）。旬・月は MIN_MONTHS か月より短くしない */
export function rangeOf(rows: readonly ScheduleRow[], today: string, view: View): { start: string; days: number } {
  let lo = today;
  let hi = today;
  for (const r of rows) {
    for (const d of [r.start, r.end]) {
      if (!d) continue;
      if (d < lo) lo = d;
      if (d > hi) hi = d;
    }
  }
  let start = addDays(lo, -7);
  if (view === "jun") start = monthStartOf(lo);
  else if (view === "month") start = monthStartOf(start);
  else start = addDays(start, -((weekdayOf(start) + 6) % 7));
  let end = addDays(hi, 28);
  if (dayNo(end) - dayNo(start) < 56) end = addDays(start, 56);
  if (view !== "day") {
    end = monthEndOf(end);
    const least = monthsEnd(start, MIN_MONTHS[view]);
    if (end < least) end = least;
  }
  return { start, days: dayNo(end) - dayNo(start) + 1 };
}

/** その日の左端の x（範囲の最初の日＝0） */
export const xOf = (ymd: string, start: string, px: number): number => (dayNo(ymd) - dayNo(start)) * px;
/** x → 日（四捨五入しない。何日目の枠に入るか） */
export const dayAt = (x: number, start: string, px: number): string => ymdOfDayNo(dayNo(start) + Math.floor(x / px));

/** 開始〜終わりのうち、稼働日の続き（塊）。紙（#104）と同じ区切りにするため packages/contract に1つ */
export { workRuns } from "@genbaryoku/contract";

/** 見出しの行の日付の一覧（範囲の全部の日） */
export function daysOf(start: string, days: number): string[] {
  const s = dayNo(start);
  return Array.from({ length: days }, (_, i) => ymdOfDayNo(s + i));
}

/* ---------------- 工程・担当の列の幅（#102 §5.1 の2：中身に合わせる） ---------------- */

/** 工程・担当の列の幅の下限と上限（中身に合わせるとき） */
export const NAME_MIN = 168;
export const NAME_MAX = 320;
/** 手で変えるときの下限 */
export const NAME_MANUAL_MIN = 120;
/** 左の表の固定の列：つかみ 24・開始 58・日数 46・進み 60・⋮ 40 と すき間（2px × 5） */
export const FIXED_W = 24 + 58 + 46 + 60 + 40 + 2 * 5;
/** 表の列（帯の ⋮「表示設定」。#296、docs/102 §2.4.1）：開始・日数・状況（進み）を出すか。人は人数の列（CREW_W）のまま別に */
export type TableCols = { start: boolean; days: boolean; state: boolean };
export const ALL_COLS: TableCols = { start: true, days: true, state: true };
/** 隠した列の幅（すき間 2px ごと）＝工程・担当の列に回す幅 */
export const hiddenColsW = (c: TableCols): number => (c.start ? 0 : 58 + 2) + (c.days ? 0 : 46 + 2) + (c.state ? 0 : 60 + 2);
/** 左の表の grid の列（styles.css の .sch-hl・.sch-l の --sch-cols） */
export const colsTemplate = (c: TableCols, crew: boolean, amount: boolean): string =>
  ["24px", "minmax(0,1fr)", c.start ? "58px" : "", c.days ? "46px" : "", c.state ? "60px" : "", crew ? "44px" : "", amount ? "104px" : "", "40px"].filter(Boolean).join(" ");
/** 人数の列（44＋すき間 2）・金額の列（104＋すき間 2） */
export const CREW_W = 44 + 2;
export const AMOUNT_W = 104 + 2;
/** 欄の余白と枠（左右の padding 5 ＋ 枠 1、`.sch-in`） */
const FIELD_CHROME = 12;

/**
 * 画面の文字の大きさ（#115、docs/44 §2）：「大」の N px を、いまの大きさ（getComputedStyle で読む --fs-N）に。14 以下は変わらない（下限）。
 * 中・小では 16 → 15・14 など。CSS の font-size: var(--fs-N) と同じ値になる
 */
export function fsPx(n: number): number {
  if (n < 15 || typeof document === "undefined") return n;
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(`--fs-${n}`));
  return Number.isFinite(v) && v > 0 ? v : n;
}

let measureCtx: CanvasRenderingContext2D | null | undefined;
/** 文字の幅（px）。canvas の measureText。フォントは画面の本文と同じ（document.fonts.ready のあと・文字の大きさを変えたあとに測り直す）。size は「大」の px */
export function textWidth(text: string, size: number, bold = false): number {
  if (!text) return 0;
  if (measureCtx === undefined) measureCtx = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  const px = fsPx(size);
  if (!measureCtx) return text.length * px;
  const family = typeof document === "undefined" ? "sans-serif" : getComputedStyle(document.body).fontFamily || "sans-serif";
  measureCtx.font = `${bold ? 700 : 400} ${px}px ${family}`;
  return measureCtx.measureText(text).width;
}

/** 測るもの：文字・大きさ・太字・横に並ぶもの（印・ボタン）の幅 */
export type NameMeasure = { text: string; size: number; bold?: boolean; extra?: number };

/**
 * 工程・担当の列の幅：いちばん長いものを測って ＋12px し、168〜320px に収める（docs/39 §5.1 の2。
 * 測るのは 工程名（16px 太）／担当の行（略称・「・n人」・休日も・雨でも の印）／見出し（太＋たたむボタン 28px）とその説明。メモは数えない）
 */
export function nameColumnWidth(
  items: readonly NameMeasure[],
  measure: (m: NameMeasure) => number = (m) => textWidth(m.text, m.size, m.bold),
  range: { min?: number; max?: number } = {},
): number {
  let w = 0;
  for (const m of items) w = Math.max(w, measure(m) + (m.extra ?? 0));
  return Math.max(range.min ?? NAME_MIN, Math.min(range.max ?? NAME_MAX, Math.ceil(w) + 12));
}

/** 工程表の行から、工程・担当の列で測るもの（担当の名前は略称。重なりの印は「重なり」。2行目の人数は #297 でやめた） */
export function nameMeasuresOf(rows: readonly ScheduleRow[], assigneeText: (r: ScheduleRow) => string, marks: { overlap?: ReadonlySet<string> } = {}): NameMeasure[] {
  const out: NameMeasure[] = [];
  // 「休日も」「雨でも」の印：3字（14px）＋ padding 12 ＋ 枠 2 ＋ 左のすき間 4
  const badge = 3 * 14 + 12 + 2 + 4;
  for (const r of rows) {
    if (r.kind === "memo") continue;
    if (r.kind === "heading") {
      // たたむボタン 28 ＋ すき間 2。#291 から見出しも 開始・日数の欄を持つ（列をまたがない）。2行目は 担当＋説明
      out.push({ text: r.name, size: 16, bold: true, extra: 28 + 2 + FIELD_CHROME });
      const asg = assigneeText(r);
      if (r.note || asg) out.push({ text: [asg, r.note].filter(Boolean).join("　"), size: 14, extra: 28 + 2 + FIELD_CHROME * (asg && r.note ? 2 : 1) });
      continue;
    }
    // 節目は ◆（13 ＋ 左右 6）
    out.push({ text: r.name, size: 16, bold: true, extra: FIELD_CHROME + (r.kind === "milestone" ? 19 : 0) });
    const asg = assigneeText(r);
    const badges = r.kind === "task" ? (r.workOffDays ? badge : 0) + (r.rainOk ? badge : 0) + (marks.overlap?.has(r.id) ? badge : 0) : 0;
    if (asg || badges) out.push({ text: asg, size: 14, extra: FIELD_CHROME + badges });
  }
  return out;
}

/* ---------------- ドラッグで動かしたときの形（#100。3回目でスマホの表と同じものを使う） ---------------- */

/**
 * バーを dd 日動かしたときの行の直し（move＝全体・resr＝右端で日数・resl＝左端で開始と日数）。
 * 「休みの日も作業する」工程は休みの無い暦。そろえる（押し出し）のは呼ぶ側の normalizeSchedule
 */
export function dragPatch(r: ScheduleRow, t: "move" | "resl" | "resr", dd: number, cal: WorkCalendar): Partial<ScheduleRow> | null {
  if (!r.start) return null;
  const rc = calendarForRow(r, cal);
  if (t === "move") {
    const s = addDays(r.start, dd);
    // その{task}の休みの指定のある日に置いたら、その日のまま（保存のときに指定が外れる＝開始が後ろへ逃げない。#296、docs/102 §5.9）
    return r.kind === "task" ? { start: snapStartOf(r, s, cal) } : { start: s, end: s };
  }
  if (r.kind !== "task") return null;
  if (t === "resr") {
    const end = addDays(r.end ?? r.start, dd);
    return { days: Math.max(1, Math.min(999, workDaysBetween(r.start, end < r.start ? r.start : end, rc))) };
  }
  const end = r.end ?? r.start;
  let s = snapStartOf(r, addDays(r.start, dd), cal);
  if (s > end) s = snapToWorkDay(end, rc, -1);
  // 指定の外れる開始の日も1日に数える（終わりは動かさない）
  const own = s !== r.start && rc.force?.get(s) === false ? 1 : 0;
  return { start: s, days: Math.max(1, workDaysBetween(s, end, rc) + own) };
}

/* ---------------- 全体の工程表の期間（#102 §5.3.1） ---------------- */

/** その週の月曜 */
export const mondayOf = (ymd: string): string => addDays(ymd, -((weekdayOf(ymd) + 6) % 7));
/**
 * 全体の工程表で読む期間（API は 400日まで）：日＝前の週の月曜から 10週、旬＝2週前の月の1日から6か月（#291 §2.1.1）、月＝前の月の1日から 13か月
 */
export function overviewRangeOf(anchor: string, view: View): { start: string; days: number } {
  if (view === "month") {
    const y = Number(anchor.slice(0, 4));
    const m = Number(anchor.slice(5, 7)) - 1; // 0 始まり
    const first = (yy: number, mm: number) => {
      const k = yy * 12 + mm;
      return `${Math.floor(k / 12)}-${String((k % 12) + 1).padStart(2, "0")}-01`;
    };
    const start = first(y, m - 1);
    const end = addDays(first(y, m + 12), -1);
    return { start, days: dayNo(end) - dayNo(start) + 1 };
  }
  if (view === "jun") {
    const start = monthStartOf(addDays(anchor, -14));
    const end = monthsEnd(start, MIN_MONTHS.jun);
    return { start, days: dayNo(end) - dayNo(start) + 1 };
  }
  return { start: addDays(mondayOf(anchor), -7), days: 7 * 10 };
}

/**
 * 旬・月の表示のバー（#290・#291）：休みで区切らず1本。済んだ所は稼働日で数えた分を、休みを含む暦の日に直す（Gantt.tsx の Bar・紙の joinRuns と同じ）。
 * 戻り値の done は 1本のバーの最初の日からの暦の日数（小数あり）
 */
export function joinedRun(runs0: readonly { from: string; to: string }[], progress: number): { from: string; to: string; done: number } | null {
  if (!runs0.length) return null;
  const total = runs0.reduce((n, s) => n + dayNo(s.to) - dayNo(s.from) + 1, 0) || 1;
  let left = (total * progress) / 100;
  let cal0 = 0;
  for (const u of runs0) {
    const len = dayNo(u.to) - dayNo(u.from) + 1;
    if (left <= 0) break;
    if (left < len) {
      cal0 = dayNo(u.from) - dayNo(runs0[0]!.from) + left;
      left = 0;
      break;
    }
    left -= len;
    cal0 = dayNo(u.to) - dayNo(runs0[0]!.from) + 1;
  }
  return { from: runs0[0]!.from, to: runs0[runs0.length - 1]!.to, done: cal0 };
}
